import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useCardReader } from 'Src/feature/card-reader/useCardReader';
import { CardReadSuccessOptions } from 'Src/feature/card-reader/useCardReaderStore';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalSubTitle } from 'Src/feature/modal/ModalSubTitle';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { PaymentIcon } from 'Src/feature/payment/PaymentIcon';
import { useCheckPagePayment } from 'Src/feature/payment/useCheckPagePayment';

const sectionId = 'GiftCardLookupModal';

interface FormModel {
  account: string;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

const TEST_ID = {
  root: `GiftCardLookupModal`,
  cta: {
    cancel: `GiftCardLookupModal-cancel`,
    lookup: `GiftCardLookupModal-lookup`,
  }
};

interface GiftCardLookupModalProps {
  control: ReturnType<typeof useCheckPagePayment>['modal']['giftCardLookup'],
}
const GiftCardLookupModalContent: FC<GiftCardLookupModalProps> = ({ control }) => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });

  const form = useForm<FormModel>({
    schema: object().shape({
      account: string().label(t('giftCard.labels.accountNumber')).required(),
    }),
  });

  const ref = useRef({
    // The giftcard payment method card reader
    // Should only be transacting for "gift card lookup"
    cardReader: useCardReader({ id: sectionId }),
    onCardRead: (read: CardReadSuccessOptions) => console.info('this will get reset each render just in case to stay stable', { read })
  });

  ref.current.onCardRead = (read) => {
    if (!read || !read.pax) {
      log.error('onCardRead()', 'no pax data received');
      return false;
    }
    if (ref.current.cardReader.is.giftCardInvalid(read.pax)) {
      log.error('onCardRead()', 'giftCardInvalid');
      read.reInit();
      return;
    }
    control.onLookupCardRead(read);
  };

  const onManualAccountLookup = () => {
    control.onManualAccountLookup(form.action.getValues().account.toString());
  };

  const { isOpen } = control;

  useEffect(() => {
    if (isOpen) {
      ref.current.cardReader.init({
        mode: 'msr',
        // should not need an amount for looking up the gift card
        amount: 0,
        onSuccess: ref.current.onCardRead,
        onTimeout: () => true,
        onError: () => true,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const { dispose } = ref.current.cardReader;
    return () => {
      void dispose();
    };
  }, []);


  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('labels.giftCardLookup')} />
        <ModalSubTitle copy={t('giftCard.labels.swipeOrEnter')} />
      </ModalHeader>
      <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
        <Flex horizontal alignItems="center" gap="sm">
          <PaymentIcon paymentType="GiftCard" size="md" />
          <Copy variant="body" value={t('giftCard.instructionOnlyActive')} />
        </Flex>
        <Form usedForm={form} omitChangeTracking>
          <Field type="numberInput" name="account" />
        </Form>
      </ModalContent>
      <ModalActions>
        <ModalAction
          data-testid={TEST_ID.cta.cancel}
          color="secondary"
          label={t('labels.cancel')}
          onClick={() => control.close('on-cancel-click')}
        />
        <ModalAction
          data-testid={TEST_ID.cta.lookup}
          color="primary"
          label={t('giftCard.labels.lookup')}
          onClick={onManualAccountLookup}
          disabled={!form.state.isValid}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const GiftCardLookupModal: FC<GiftCardLookupModalProps> = ({ control }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={control.isOpen}
    onBackDropClick={() => control.close('on-backdrop-click')}
  >
    <GiftCardLookupModalContent control={control} />
  </Modal>
);

export { GiftCardLookupModal, TEST_ID };
export type { GiftCardLookupModalProps };
